import { FilterValues } from 'app/modules/reports/features/search/full-page-search-forms/filter-values'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import React, { useEffect, useState } from 'react'
import { Card } from 'react-bootstrap'
import Button from 'app/modules/shared/components/button'

export type FullPageDialogFooterType = {
  title?: string
  description?: string
  isOpen: boolean
  body: JSX.Element
  onClose: (...args: any[]) => void
  onApply?: (...args: any[]) => void
  onRemoveFilter?: (...args: any[]) => void
  onClearSelection?: (...args: any[]) => void
  filteredList: string[]
  contentLength?: number
  enableSubmit?: boolean
  filterSummaryLabel?: string
  noFilterLabel?: string
  applyButtonLabel?: string
}

const FullPageDialog = ({
  title,
  description,
  isOpen,
  body,
  onClose,
  onApply,
  filteredList = [],
  contentLength = 0,
  onRemoveFilter,
  onClearSelection,
  enableSubmit,
  filterSummaryLabel,
  noFilterLabel,
  applyButtonLabel,
}: FullPageDialogFooterType) => {
  const [showAllFilters, setShowAllFilters] = useState(false)

  const { _t } = useTypedTranslation()

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open')
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.body.classList.remove('modal-open')
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const handleClose = () => {
    document.body.classList.remove('modal-open')
    onClose()
  }

  const handleApply = () => {
    document.body.classList.remove('modal-open')
    onApply?.()
    onClose()
  }

  const summary = filteredList.length ? (
    <>
      <div className="filter-name">{filterSummaryLabel}</div>
      <div className="filter-values">
        <FilterValues
          values={filteredList}
          showAll={showAllFilters}
          setShowAll={setShowAllFilters}
          onRemove={onRemoveFilter}
        />
      </div>
    </>
  ) : (
    <span className="text-main text-i text-muted">{noFilterLabel}</span>
  )

  return (
    <div className={isOpen ? '' : 'd-none'}>
      <div className="overlay"></div>
      <div className="dialog-wrapper">
        <Card className="dialog-full-page">
          <Card.Body className="dialog-card-body">
            <div className="dialog-header">
              <div className="dialog-title">
                <h3 className="m-0">{title}</h3>
                <span className="text-main text-muted text-i align-self-end">{description}</span>
              </div>
              <Button id="full-page-dialog-close" variant="tertiary" onClick={handleClose}>
                <i className="ds3 ds3-icon ds3-close"></i>
              </Button>
            </div>
            <div className="dialog-body">{body}</div>
            <div className="dialog-footer">
              <div className="dialog-filter-summary">
                {contentLength ? (
                  <div className="dialog-filter-label">
                    <h3>{filteredList.length}</h3>&nbsp;
                    <span className="text-main text-muted">{`/ ${contentLength}`}</span>
                  </div>
                ) : null}
                {summary}
              </div>
              <div className="dialog-button-group">
                {filteredList.length ? (
                  <Button id="full-page-dialog-clear-all" variant="tertiary" onClick={onClearSelection}>
                    {_t('clear-all')}
                  </Button>
                ) : null}
                <Button id="full-page-dialog-cancel" variant="secondary" onClick={onClose}>
                  {_t('cancel')}
                </Button>
                <Button
                  id="full-page-dialog-apply"
                  disabled={!(filteredList.length || enableSubmit)}
                  variant="primary"
                  onClick={handleApply}
                >
                  {applyButtonLabel}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}

export default FullPageDialog
